import React, {
    FunctionComponent,
    SyntheticEvent,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'

import { AxiosResponse } from 'axios'

import Checkbox from '../../App/Components/Checkbox/Checkbox'
import TextInput from '../../App/Components/Form/TextInput/TextInput'
import Spinner from '../../App/Components/Spinner/Spinner'

import SignupClient from './Client/SignupClient'
import { SubmitError } from './components/SubmitError/SubmitError'
import IProps from './IProps'
import {
    Background,
    CenteredDiv,
    Form,
    Header,
    LegalFooter,
    LogoImg,
    SignupButton,
} from './style'
import {useCookies} from 'react-cookie'
import moment from 'moment'

const Signup: FunctionComponent<IProps> = (props) => {
    const { t, i18n } = useTranslation()
    const { executeRecaptcha } = useGoogleReCaptcha()

    const [name, setName] = useState<string>('')
    const [nameError, setNameError] = useState<null | string>(null)
    const [companyName, setCompanyName] = useState<string>('')
    const [companyNameError, setCompanyNameError] = useState<null | string>(
        null
    )
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<null | string>(null)
    const [phone, setPhone] = useState<string>('')
    const [phoneError, setPhoneError] = useState<null | string>(null)
    const [newsletter, setNewletter] = useState<boolean>(false)
    const [countryCode, setCountryCode] = useState<string>('BE')
    const [submitState, setSubmitState] = useState<
        'idle' | 'busy' | 'error' | 'success'
    >('idle')
    const [cookies, setCookie] = useCookies();

    useEffect(() => {
        SignupClient.getCountryCode().then((newCountryCode) =>
            setCountryCode(newCountryCode)
        )

        const referralCode: string | null = (new URLSearchParams(window.location.search)).get('referral');
        if (referralCode !== null && referralCode !== '') {
            const expires = moment().add(1, 'M').toDate()
            setCookie(
                'signup_referral',
                referralCode,
                {
                    expires: expires
                }
            );
        }
    }, [])

    useEffect(() => {
        i18n.changeLanguage(props.language)
    }, [props.language])

    const handleReCaptchaVerify = useCallback(async (): Promise<
        string | undefined
    > => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available')
            return
        }

        return executeRecaptcha('signupSubmit')
    }, [executeRecaptcha])

    // You can use useEffect to trigger the verification as soon as the component being loaded
    useEffect(() => {
        handleReCaptchaVerify()
    }, [handleReCaptchaVerify, executeRecaptcha])

    const isValid = () => {
        let errorCounter = 0
        if (name === '') {
            setNameError(t('Please fill in your name'))
            errorCounter++
        } else {
            setNameError('')
        }

        if (companyName === '') {
            setCompanyNameError(t('Please fill in your company name'))
            errorCounter++
        } else {
            setCompanyNameError('')
        }

        if (phone === '') {
            setPhoneError(t('Please fill in your phone number'))
            errorCounter++
        } else {
            setPhoneError('')
        }

        if (email === '' || !/^\S+@\S+$/i.test(email)) {
            setEmailError(t('Please fill in your e-mail address'))
            errorCounter++
        } else {
            setEmailError('')
        }

        return 0 === errorCounter
    }

    const onFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!isValid()) {
            return
        }

        setSubmitState('busy')
        handleReCaptchaVerify()
            .then((token) => {
                if (!token) {
                    return
                }

                SignupClient.signup(
                    name,
                    companyName,
                    email,
                    phone,
                    newsletter,
                    countryCode,
                    props.language,
                    token,
                    typeof cookies.signup_referral === 'undefined' ? null : cookies.signup_referral
                )
                    .then((response: AxiosResponse) => {
                        setSubmitState('success')
                    })
                    .catch((err) => {
                        setSubmitState('error')
                    })
            })
            .catch(() => setSubmitState('error'))

        return false
    }

    return (
        <Background>
            <CenteredDiv>
                <Header>
                    <LogoImg
                        src={
                            'https://user-images.githubusercontent.com/576657/159713528-0d06e5f6-8c96-4680-8f9a-8504dae86989.png'
                        }
                        alt={'Optios'}
                    />
                    <h1>{t('Welcome to Optios')}</h1>
                    <p>
                        {t(
                            'Try Optios now for free and without obligation! Do you already have an account?'
                        )}
                        &nbsp;
                        <a
                            style={{
                                color: '#ffffff',
                                textDecoration: 'underline',
                            }}
                            href={'/login'}
                        >
                            {t('Login now')}
                        </a>
                    </p>
                </Header>
                {submitState === 'busy' ? (
                    <Spinner />
                ) : submitState === 'error' ? (
                    <SubmitError setSubmitState={setSubmitState} />
                ) : submitState === 'success' ? (
                    <div className={'status-message'}>
                        <h2>
                            {t(
                                'An email for confirmation has been sent to your address.'
                            )}
                        </h2>
                        <h2>
                            {t(
                                'Please follow the instructions to activate your account.'
                            )}
                        </h2>
                    </div>
                ) : (
                    <>
                        <Form>
                            <form onSubmit={onFormSubmit} id="simpleForm">
                                <TextInput
                                    label={t('Your name')}
                                    value={name}
                                    error={nameError}
                                    onChange={(
                                        e: SyntheticEvent<HTMLInputElement>
                                    ) => setName(e.currentTarget.value)}
                                    color={'#ffffff'}
                                    background={'transparent'}
                                    inputColor={'#000000'}
                                    inputBackground={'#ffffff'}
                                    inputPadding={'12px'}
                                />
                                <TextInput
                                    label={t('Company name')}
                                    value={companyName}
                                    error={companyNameError}
                                    onChange={(
                                        e: SyntheticEvent<HTMLInputElement>
                                    ) => setCompanyName(e.currentTarget.value)}
                                    color={'#ffffff'}
                                    background={'transparent'}
                                    inputColor={'#000000'}
                                    inputBackground={'#ffffff'}
                                    inputPadding={'12px'}
                                />
                                <TextInput
                                    label={t('E-mail')}
                                    value={email}
                                    error={emailError}
                                    onChange={(
                                        e: SyntheticEvent<HTMLInputElement>
                                    ) => setEmail(e.currentTarget.value)}
                                    color={'#ffffff'}
                                    background={'transparent'}
                                    inputColor={'#000000'}
                                    inputBackground={'#ffffff'}
                                    inputPadding={'12px'}
                                />
                                <TextInput
                                    label={t('Phone')}
                                    value={phone}
                                    error={phoneError}
                                    onChange={(
                                        e: SyntheticEvent<HTMLInputElement>
                                    ) => setPhone(e.currentTarget.value)}
                                    color={'#ffffff'}
                                    background={'transparent'}
                                    inputColor={'#000000'}
                                    inputBackground={'#ffffff'}
                                    inputPadding={'12px'}
                                />
                                <Checkbox
                                    label={t(
                                        'Receive updates and news via e-mail'
                                    )}
                                    isChecked={newsletter}
                                    onChange={(checked) =>
                                        setNewletter(checked)
                                    }
                                    color={'#ffffff'}
                                />
                                <div
                                    className="g-recaptcha"
                                    data-sitekey="6Le_jJoeAAAAAK0LPENUkgFXNT3C5-ezN8yoN1pw"
                                    data-size="invisible"
                                />
                                <SignupButton
                                    id="signupSubmitButton"
                                    type={'submit'}
                                >
                                    {t('Start now')}
                                </SignupButton>
                                <LegalFooter>
                                    {t('By signing up you agree to our')}
                                    &nbsp;
                                    <a
                                        style={{
                                            color: '#ffffff',
                                            textDecoration: 'underline',
                                        }}
                                        href={
                                            'https://www.optios.net/algemene-voorwaarden'
                                        }
                                    >
                                        {t('terms and conditions')}
                                    </a>
                                </LegalFooter>
                            </form>
                        </Form>
                    </>
                )}
            </CenteredDiv>
        </Background>
    )
}

export default Signup
